import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight, Film, Video, Music, Captions, Database } from 'lucide-react';
import { eventsApi } from '@/api/client';
import { useRecordings } from '@/hooks/useProcessing';
import type { Recording, RecordingFlow } from '@/types/processing';
import {
  Card,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  formatDuration,
  formatWallclock,
  inputClass,
} from './shared';

function programmeTrackIcon(flow: RecordingFlow) {
  if (flow.format.endsWith(':video')) return Video;
  if (flow.format.endsWith(':audio')) return Music;
  return Captions;
}

function FlowChips({ flows }: { flows: RecordingFlow[] }) {
  const programme = flows.filter((f) => f.kind === 'programme');
  const data = flows.filter((f) => f.kind !== 'programme');
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {programme.map((f) => {
        const Icon = programmeTrackIcon(f);
        return (
          <span
            key={f.flowId}
            title={f.label ?? f.format}
            className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] rounded bg-jeket-gold/10 text-jeket-gold"
          >
            <Icon size={11} />
            {f.label ?? f.format.split(':').pop()}
          </span>
        );
      })}
      {data.length > 0 && (
        <span
          title={data.map((f) => f.kind).join(', ')}
          className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] rounded bg-jeket-black-elevated text-jeket-silver-muted"
        >
          <Database size={11} />
          {data.length} data flow{data.length === 1 ? '' : 's'}
        </span>
      )}
    </div>
  );
}

function recordingTitle(rec: Recording, eventName?: string): string {
  return rec.label || eventName || rec.streamId;
}

export default function RecordingsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const eventId = searchParams.get('eventId') ?? undefined;

  const { data: recordings, isLoading, isError, error } = useRecordings(eventId);
  const { data: events } = useQuery({
    queryKey: ['events', 'processing-filter'],
    queryFn: () => eventsApi.list(),
    staleTime: 60_000,
  });

  const eventNames = useMemo(
    () => new Map((events ?? []).map((e) => [e.id, e.name])),
    [events],
  );

  const sorted = useMemo(
    () => [...(recordings ?? [])].sort((a, b) => (b.start ?? 0) - (a.start ?? 0)),
    [recordings],
  );

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <PageHeader
        title="Recordings"
        subtitle="Programme recordings retained in the cloud store, with the results produced on them."
        actions={
          <select
            aria-label="Filter by event"
            value={eventId ?? ''}
            onChange={(e) => {
              const next = new URLSearchParams(searchParams);
              if (e.target.value) next.set('eventId', e.target.value);
              else next.delete('eventId');
              setSearchParams(next, { replace: true });
            }}
            className={inputClass}
          >
            <option value="">All events</option>
            {(events ?? []).map((ev) => (
              <option key={ev.id} value={ev.id}>
                {ev.name}
              </option>
            ))}
          </select>
        }
      />

      <Card className="overflow-hidden">
        <div className="hidden md:flex items-center gap-4 px-4 py-2.5 border-b border-jeket-gold/20 text-xs font-medium text-jeket-silver-muted uppercase tracking-wider">
          <div className="flex-1">Recording</div>
          <div className="w-44">Started</div>
          <div className="w-24">Duration</div>
          <div className="w-64">Flows</div>
          <div className="w-5" />
        </div>

        {isLoading ? (
          <LoadingState label="Loading recordings…" />
        ) : isError ? (
          <ErrorState title="Couldn't load recordings" error={error} />
        ) : sorted.length === 0 ? (
          <EmptyState
            icon={Film}
            title={eventId ? 'No recordings for this event' : 'No recordings yet'}
            hint="A recording starts as soon as a site publishes its programme to the cloud."
          />
        ) : (
          <ul>
            {sorted.map((rec) => {
              const eventName = rec.eventId ? eventNames.get(rec.eventId) : undefined;
              const duration = rec.start != null && rec.end != null ? rec.end - rec.start : null;
              return (
                <li key={rec.sourceId} className="border-b border-jeket-gold/10 last:border-b-0">
                  <Link
                    to={`/media/recordings/${encodeURIComponent(rec.sourceId)}`}
                    className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4 px-4 py-3 hover:bg-jeket-black-elevated/50 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-jeket-silver-bright truncate">{recordingTitle(rec, eventName)}</p>
                      <p className="text-xs text-jeket-silver-muted truncate">
                        {eventName && rec.label ? `${eventName} · ` : ''}
                        {rec.streamId}
                      </p>
                    </div>
                    <div className="md:w-44 text-xs text-jeket-silver-muted tabular-nums">
                      {formatWallclock(rec.start)}
                    </div>
                    <div className="md:w-24 text-xs text-jeket-silver-muted tabular-nums">
                      {formatDuration(duration)}
                    </div>
                    <div className="md:w-64">
                      <FlowChips flows={rec.flows} />
                    </div>
                    <ChevronRight size={16} className="hidden md:block w-5 text-jeket-silver-muted" />
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </Card>
    </div>
  );
}
